import {
  useAuth,
  useCampaignStore,
  useInitCurrentCampaign,
} from '@lidea/shared/data-access/core';
import { useMe } from '@lidea/shared/data-access/users';
import { useEffect, useRef, useState } from 'react';

import { SyncProgressModal } from './components/sync-progress-modal/sync-progress-modal';
import type { SetupCacheControls } from './hooks/setup-cache-controls';
import { useSyncProgressModal } from './hooks/use-sync-progress-modal';
import {
  clearSkipAutoSyncSession,
  OFFLINE_BOOTSTRAP_RESET_KEY,
  OFFLINE_CACHE_CLEARED_EVENT,
  shouldSkipAutoSyncSession,
} from './sync-job/clear-offline-cache';
import { OFFLINE_SCOPE_CHANGED_EVENT } from './sync-job/offline-scope-session';
import { getSyncJob } from './sync-job/sync-job-store';
import { isResumableSyncPhase } from './sync-job/types';
import { isWideOfflineRole } from './sync-job/wide-roles';

export { OPEN_SYNC_PROGRESS_EVENT, openSyncProgressModal } from './sync-events';

export type SetupCacheProps = {
  controls: SetupCacheControls;
};

export const SetupCache = ({ controls }: SetupCacheProps) => {
  const { token } = useAuth();
  const me = useMe();
  const campaignId = useCampaignStore((s) => s.campaignId);
  useInitCurrentCampaign();
  const bootstrappedRef = useRef(false);
  const [bootstrapNonce, setBootstrapNonce] = useState(0);

  const {
    startAutoSync,
    resumeJob,
    isScopeAlreadySynced,
    isSettingUpCache,
    job,
  } = controls;

  const { progressOpen, dismissProgressModal } = useSyncProgressModal({
    isSettingUpCache,
    job,
  });

  useEffect(() => {
    const onCacheCleared = () => {
      bootstrappedRef.current = false;
      setBootstrapNonce((n) => n + 1);
    };
    window.addEventListener(OFFLINE_CACHE_CLEARED_EVENT, onCacheCleared);
    return () => {
      window.removeEventListener(OFFLINE_CACHE_CLEARED_EVENT, onCacheCleared);
    };
  }, []);

  useEffect(() => {
    const onScopeChanged = () => {
      bootstrappedRef.current = false;
      setBootstrapNonce((n) => n + 1);
    };
    window.addEventListener(OFFLINE_SCOPE_CHANGED_EVENT, onScopeChanged);
    return () => {
      window.removeEventListener(OFFLINE_SCOPE_CHANGED_EVENT, onScopeChanged);
    };
  }, []);

  useEffect(() => {
    if (window.sessionStorage.getItem(OFFLINE_BOOTSTRAP_RESET_KEY)) {
      window.sessionStorage.removeItem(OFFLINE_BOOTSTRAP_RESET_KEY);
      clearSkipAutoSyncSession();
      bootstrappedRef.current = false;
    }

    if (!me.data?.role || !campaignId || bootstrappedRef.current) {
      return;
    }
    if (!token || !me.isSuccess || !me.data) {
      return;
    }

    const bootstrap = async () => {
      bootstrappedRef.current = true;
      const existing = await getSyncJob();
      if (existing && isResumableSyncPhase(existing.status)) {
        await resumeJob();
        return;
      }

      const role = me.data!.role;

      if (isWideOfflineRole(role)) {
        return;
      }

      if (shouldSkipAutoSyncSession()) {
        return;
      }

      if (isScopeAlreadySynced()) {
        return;
      }

      await startAutoSync({
        campaignId,
        userRole: role,
      });
    };

    bootstrap();
  }, [
    token,
    me.data?.role,
    me.isSuccess,
    campaignId,
    startAutoSync,
    resumeJob,
    isScopeAlreadySynced,
    bootstrapNonce,
  ]);

  return (
    <SyncProgressModal
      isOpen={progressOpen}
      onClose={dismissProgressModal}
      {...controls}
    />
  );
};
